import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  Box,
  Button,
  Checkbox,
  Collapse,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Page from "./Page";
import Card from "../layout/Card";
import DateField from "../elements/Date";
import DataTable from "../elements/DataTable";
import CallDocketModal from "../modals/CallDocketModal";

import useData from "../../hooks/useData";
import useCall from "../../hooks/useCall";
import usePage from "../../hooks/usePage";
import { getId } from "../../utils/idHelpers";

const DEFAULT_FILTER = {
  clientId: "",
  projectId: "",
  retailer: "",
  banner: "",
  locationNumber: "",
  payPeriodId: "",
  paid: "",
  locationId: "",
  callId: "",
  territory: "",
  rep: "",
  dateFrom: "",
  dateTo: "",
  callStatus: [],
  billToClient: "",
  province: "",
  city: "",
};

const CallList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUserRole = useSelector((state) => state.authentication.role);
  const canViewSensitiveFields = ["finance", "sysadmin"].includes(
    currentUserRole,
  );

  const { executeAction, addToWorkQueue, getFromServer } = useData();
  const { getCallById, getCallFullListById } = useCall();
  const { showMessage } = usePage();

  const contextProjectId = location.state?.projectId || "";
  const contextClientId = location.state?.clientId || "";
  const isProjectContext = Boolean(contextProjectId);
  const projectId = isProjectContext ? contextProjectId : "";
  const clientId = isProjectContext ? contextClientId : "";

  const [projectName, setProjectName] = useState("calls");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [draftFilter, setDraftFilter] = useState({
    ...DEFAULT_FILTER,
    clientId,
    projectId,
  });
  const [appliedFilter, setAppliedFilter] = useState({
    ...DEFAULT_FILTER,
    clientId,
    projectId,
  });
  const [hasApplied, setHasApplied] = useState(false);

  const [options, setOptions] = useState({
    clients: [],
    projects: [],
    retailers: [],
    banners: [],
    payPeriods: [],
    territories: [],
    reps: [],
    provinces: [],
    cities: [],
    callStatuses: [],
  });

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  const [orderBy, setOrderBy] = useState("yyyymmdd");
  const [order, setOrder] = useState("desc");

  const [selectedCall, setSelectedCall] = useState(null);
  const [docketOpen, setDocketOpen] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  const projectOptions = useMemo(() => {
    if (!draftFilter.clientId) {
      return options.projects;
    }

    return options.projects.filter(
      (item) => String(item.clientId || "") === String(draftFilter.clientId),
    );
  }, [options.projects, draftFilter.clientId]);

  const bannerOptions = useMemo(() => {
    if (!draftFilter.retailer) {
      return options.banners;
    }

    return options.banners.filter(
      (item) => String(item.retailerId || "") === String(draftFilter.retailer),
    );
  }, [options.banners, draftFilter.retailer]);

  useEffect(() => {
    const loadContext = async () => {
      if (isProjectContext) {
        if (!projectId) {
          showMessage("Missing project context", "error");
          navigate("/project");
          return;
        }

        const project = await getFromServer({
          entityName: "Project",
          id: projectId,
        });
        if (project?.name) {
          setProjectName(project.name);
        }
      } else {
        setProjectName("calls");
      }

      await loadFilterOptions();
    };

    loadContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isProjectContext]);

  useEffect(() => {
    if (!hasApplied) return;
    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isProjectContext,
    hasApplied,
    appliedFilter,
    pageNumber,
    recordsPerPage,
    orderBy,
    order,
  ]);

  const loadFilterOptions = async () => {
    const results = await executeAction({
      entityName: "Call",
      action: "getCallListFilterOptions",
      data: {
        projectId,
        clientId,
        filter: draftFilter,
      },
    });

    if (results?.isSuccessful && results.data) {
      const normalizeOption = (item) => ({
        ...item,
        id: item?.id ? String(item.id) : "",
      });

      const normalizeProject = (item) => ({
        ...normalizeOption(item),
        clientId: item?.clientId ? String(item.clientId) : "",
      });

      const normalizeBanner = (item) => ({
        ...normalizeOption(item),
        retailerId: item?.retailerId ? String(item.retailerId) : "",
      });

      setOptions({
        clients: Array.isArray(results.data.clients)
          ? results.data.clients.map(normalizeOption).filter((item) => item.id)
          : [],
        projects: Array.isArray(results.data.projects)
          ? results.data.projects
              .map(normalizeProject)
              .filter((item) => item.id)
          : [],
        retailers: Array.isArray(results.data.retailers)
          ? results.data.retailers
              .map(normalizeOption)
              .filter((item) => item.id)
          : [],
        banners: Array.isArray(results.data.banners)
          ? results.data.banners.map(normalizeBanner).filter((item) => item.id)
          : [],
        payPeriods: Array.isArray(results.data.payPeriods)
          ? results.data.payPeriods
              .map(normalizeOption)
              .filter((item) => item.id)
          : [],
        territories: Array.isArray(results.data.territories)
          ? results.data.territories
              .map(normalizeOption)
              .filter((item) => item.id)
          : [],
        reps: Array.isArray(results.data.reps)
          ? results.data.reps.map(normalizeOption).filter((item) => item.id)
          : [],
        provinces: Array.isArray(results.data.provinces)
          ? results.data.provinces
          : [],
        cities: Array.isArray(results.data.cities) ? results.data.cities : [],
        callStatuses: Array.isArray(results.data.callStatuses)
          ? results.data.callStatuses
          : [],
      });
      return;
    }

    showMessage(
      results?.message || "Unable to load call list filters",
      "error",
    );
  };

  const loadPage = async () => {
    setLoading(true);
    const results = await executeAction({
      entityName: "Call",
      action: "getCallListPage",
      data: {
        projectId,
        clientId,
        filter: appliedFilter,
        pageNumber,
        recordsPerPage,
        orderBy,
        order,
      },
    });

    console.log("[CallList].results : ", results);

    if (results?.isSuccessful && results.data) {
      setRecords(
        Array.isArray(results.data.records) ? results.data.records : [],
      );
      setTotal(Number(results.data.total) || 0);
    } else {
      setRecords([]);
      setTotal(0);
      showMessage(results?.message || "Unable to load calls", "error");
    }
    setLoading(false);
  };

  const handleToolbarClick = (name) => {
    if (name === "project" && isProjectContext) {
      navigate("/project", { state: { projectId, clientId } });
    }
  };

  const handleApply = () => {
    setAppliedFilter({ ...draftFilter });
    setPageNumber(0);
    setHasApplied(true);
  };

  const handleReset = () => {
    const reset = {
      ...DEFAULT_FILTER,
      clientId,
      projectId,
    };
    setDraftFilter(reset);
    setAppliedFilter(reset);
    setHasApplied(false);
    setPageNumber(0);
    setRecords([]);
    setTotal(0);
  };

  const handleExport = async () => {
    const exportFilter = hasApplied ? appliedFilter : draftFilter;
    const exportProjectId = projectId || exportFilter.projectId || "";
    const exportClientId = clientId || exportFilter.clientId || "";

    const hasAtLeastOneFilter = (filterObj = {}) => {
      return Object.values(filterObj).some((value) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }

        if (typeof value === "string") {
          return value.trim().length > 0;
        }

        return value !== null && value !== undefined && value !== "";
      });
    };

    if (
      !hasAtLeastOneFilter({
        ...exportFilter,
        projectId: exportProjectId,
        clientId: exportClientId,
      })
    ) {
      showMessage("Provide at least one filter before export", "error");
      return;
    }

    try {
      setExporting(true);

      const queueResults = await addToWorkQueue("exportProjectCallList", {
        projectId: exportProjectId,
        clientId: exportClientId,
        filter: exportFilter,
        includeRateFields: canViewSensitiveFields,
        includeEmployeeNumber: canViewSensitiveFields,
      });

      if (queueResults?.isSuccessful) {
        showMessage(
          "Call list export queued. You will receive an email shortly.",
          "success",
        );
        return;
      }

      showMessage(
        queueResults?.message || "Unable to queue call list export",
        "error",
      );
    } finally {
      setExporting(false);
    }
  };

  const handleSortChange = (property, nextOrder) => {
    setOrderBy(property);
    setOrder(nextOrder);
    setPageNumber(0);
  };

  const handleOpenDocket = async (row) => {
    const callId = row?.callId || row?.id || getId(row);
    if (!callId) {
      showMessage("Unable to open call docket", "error");
      return;
    }

    const call =
      (await getCallById(callId)) || (await getCallFullListById(callId));
    if (!call) {
      showMessage("Unable to load call docket", "error");
      return;
    }

    setSelectedCall(call);
    setDocketOpen(true);
  };

  const handleDocketClose = () => {
    setDocketOpen(false);
    setSelectedCall(null);
  };

  const formatExcludeFlag = (value) => {
    if (value === true) return "Yes";
    return "";
  };

  const columns = useMemo(
    () => [
      { name: "yyyymmdd", label: "Date", width: "120px" },
      { name: "payPeriodName", label: "Pay Period", width: "170px" },
      { name: "clientName", label: "Client", width: "180px" },
      { name: "projectName", label: "Project", width: "220px" },
      { name: "retailerName", label: "Retailer", width: "180px" },
      { name: "bannerName", label: "Banner", width: "180px" },
      { name: "locationName", label: "Location", width: "220px" },
      { name: "locationNumber", label: "Location #", width: "130px" },
      { name: "streetAddress", label: "Street", width: "200px" },
      { name: "otherAddress", label: "Other", width: "160px" },
      { name: "city", label: "City", width: "140px" },
      { name: "province", label: "Province", width: "110px" },
      { name: "postal", label: "Postal", width: "110px" },
      { name: "estDuration", label: "Est. Duration", width: "130px" },
      { name: "variance", label: "Variance", width: "110px" },
      { name: "startedAt", label: "Started At", width: "160px" },
      { name: "finishedAt", label: "Finished At", width: "160px" },
      { name: "callDuration", label: "Call Duration", width: "130px" },
      { name: "mileage", label: "Mileage", width: "100px" },
      {
        name: "excludeFromPayroll",
        label: "Exclude Payroll",
        width: "130px",
        render: (row) => formatExcludeFlag(row.excludeFromPayroll),
      },
      {
        name: "excludeFromBilling",
        label: "Exclude Billing",
        width: "130px",
        render: (row) => formatExcludeFlag(row.excludeFromBilling),
      },
      ...(canViewSensitiveFields
        ? [
            {
              name: "hourlyRateResolved",
              label: "Hourly Rate",
              width: "120px",
            },
            { name: "rateSource", label: "Rate Source", width: "120px" },
          ]
        : []),
      { name: "territoryName", label: "Territory", width: "170px" },
      { name: "repName", label: "Rep", width: "160px" },
      ...(canViewSensitiveFields
        ? [{ name: "repEmployeeNumber", label: "File #", width: "110px" }]
        : []),
      { name: "callStatus", label: "Status", width: "120px" },
      {
        name: "docket",
        label: "Call Docket",
        sortable: false,
        width: "130px",
        render: (row) => (
          <Button
            variant="outlined"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              handleOpenDocket(row);
            }}
          >
            Open
          </Button>
        ),
      },
    ],
    [canViewSensitiveFields],
  );

  return (
    <>
      <Page
        pageName="calllist"
        entityName={projectName}
        includeToolbar={isProjectContext}
        items={
          isProjectContext
            ? [{ name: "project", iconName: "ArrowCircleLeft" }]
            : []
        }
        onToolItemClick={handleToolbarClick}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Card title="Filters">
            <Stack sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                >
                  Filter Criteria
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setFiltersExpanded((prev) => !prev)}
                  endIcon={
                    filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                  }
                >
                  {filtersExpanded ? "Collapse" : "Expand"}
                </Button>
              </Box>

              <Collapse in={filtersExpanded}>
                <Stack spacing={2}>
                  <Box
                    sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}
                  >
                    <FormControl
                      size="small"
                      sx={{ minWidth: 220 }}
                    >
                      <InputLabel>Client</InputLabel>
                      <Select
                        value={draftFilter.clientId}
                        label="Client"
                        disabled={isProjectContext}
                        onChange={(event) =>
                          setDraftFilter((prev) => ({
                            ...prev,
                            clientId: event.target.value,
                            projectId:
                              event.target.value === prev.clientId
                                ? prev.projectId
                                : "",
                          }))
                        }
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        {options.clients.map((item) => (
                          <MenuItem
                            key={item.id}
                            value={item.id}
                          >
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl
                      size="small"
                      sx={{ minWidth: 220 }}
                    >
                      <InputLabel>Project</InputLabel>
                      <Select
                        value={draftFilter.projectId}
                        label="Project"
                        disabled={isProjectContext}
                        onChange={(event) =>
                          setDraftFilter((prev) => ({
                            ...prev,
                            projectId: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        {projectOptions.map((item) => (
                          <MenuItem
                            key={item.id}
                            value={item.id}
                          >
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl
                      size="small"
                      sx={{ minWidth: 220 }}
                    >
                      <InputLabel>Retailer</InputLabel>
                      <Select
                        value={draftFilter.retailer}
                        label="Retailer"
                        onChange={(event) =>
                          setDraftFilter((prev) => ({
                            ...prev,
                            retailer: event.target.value,
                            banner:
                              event.target.value === prev.retailer
                                ? prev.banner
                                : "",
                          }))
                        }
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        {options.retailers.map((item) => (
                          <MenuItem
                            key={item.id}
                            value={item.id}
                          >
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl
                      size="small"
                      sx={{ minWidth: 220 }}
                    >
                      <InputLabel>Banner</InputLabel>
                      <Select
                        value={draftFilter.banner}
                        label="Banner"
                        onChange={(event) =>
                          setDraftFilter((prev) => ({
                            ...prev,
                            banner: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        {bannerOptions.map((item) => (
                          <MenuItem
                            key={item.id}
                            value={item.id}
                          >
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      size="small"
                      label="Location Number"
                      value={draftFilter.locationNumber}
                      onChange={(event) =>
                        setDraftFilter((prev) => ({
                          ...prev,
                          locationNumber: event.target.value,
                        }))
                      }
                      sx={{ minWidth: 220 }}
                    />

                    <TextField
                      size="small"
                      label="Location ID"
                      value={draftFilter.locationId}
                      onChange={(event) =>
                        setDraftFilter((prev) => ({
                          ...prev,
                          locationId: event.target.value,
                        }))
                      }
                      sx={{ minWidth: 220 }}
                    />

                    <TextField
                      size="small"
                      label="Call ID"
                      value={draftFilter.callId}
                      onChange={(event) =>
                        setDraftFilter((prev) => ({
                          ...prev,
                          callId: event.target.value,
                        }))
                      }
                      sx={{ minWidth: 220 }}
                    />

                    <FormControl
                      size="small"
                      sx={{ minWidth: 220 }}
                    >
                      <InputLabel>Pay Period</InputLabel>
                      <Select
                        value={draftFilter.payPeriodId}
                        label="Pay Period"
                        onChange={(event) =>
                          setDraftFilter((prev) => ({
                            ...prev,
                            payPeriodId: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        {options.payPeriods.map((item) => (
                          <MenuItem
                            key={item.id}
                            value={item.id}
                          >
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl
                      size="small"
                      sx={{ minWidth: 220 }}
                    >
                      <InputLabel>Paid Status</InputLabel>
                      <Select
                        value={draftFilter.paid}
                        label="Paid Status"
                        onChange={(event) =>
                          setDraftFilter((prev) => ({
                            ...prev,
                            paid: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                        <MenuItem value="unpaid">Unpaid</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl
                      size="small"
                      sx={{ minWidth: 220 }}
                    >
                      <InputLabel>Territory</InputLabel>
                      <Select
                        value={draftFilter.territory}
                        label="Territory"
                        onChange={(event) =>
                          setDraftFilter((prev) => ({
                            ...prev,
                            territory: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        {options.territories.map((item) => (
                          <MenuItem
                            key={item.id}
                            value={item.id}
                          >
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl
                      size="small"
                      sx={{ minWidth: 220 }}
                    >
                      <InputLabel>Rep</InputLabel>
                      <Select
                        value={draftFilter.rep}
                        label="Rep"
                        onChange={(event) =>
                          setDraftFilter((prev) => ({
                            ...prev,
                            rep: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        {options.reps.map((item) => (
                          <MenuItem
                            key={item.id}
                            value={item.id}
                          >
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Box sx={{ minWidth: 220 }}>
                      <DateField
                        label="Date From"
                        value={draftFilter.dateFrom}
                        onChange={(value) =>
                          setDraftFilter((prev) => ({
                            ...prev,
                            dateFrom: value,
                          }))
                        }
                      />
                    </Box>

                    <Box sx={{ minWidth: 220 }}>
                      <DateField
                        label="Date To"
                        value={draftFilter.dateTo}
                        onChange={(value) =>
                          setDraftFilter((prev) => ({ ...prev, dateTo: value }))
                        }
                      />
                    </Box>

                    <FormControl
                      size="small"
                      sx={{ minWidth: 260 }}
                    >
                      <InputLabel>Bill to Client</InputLabel>
                      <Select
                        value={draftFilter.billToClient}
                        label="Bill to Client"
                        onChange={(event) =>
                          setDraftFilter((prev) => ({
                            ...prev,
                            billToClient: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        <MenuItem value="billToClient">Bill to Client</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl
                      size="small"
                      sx={{ minWidth: 260 }}
                    >
                      <InputLabel>Call Status</InputLabel>
                      <Select
                        multiple
                        value={draftFilter.callStatus}
                        label="Call Status"
                        renderValue={(selected) => selected.join(", ")}
                        onChange={(event) =>
                          setDraftFilter((prev) => ({
                            ...prev,
                            callStatus: Array.isArray(event.target.value)
                              ? event.target.value
                              : [],
                          }))
                        }
                      >
                        {options.callStatuses.map((item) => (
                          <MenuItem
                            key={item.id}
                            value={item.id}
                          >
                            <Checkbox
                              checked={draftFilter.callStatus.includes(item.id)}
                            />
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl
                      size="small"
                      sx={{ minWidth: 220 }}
                    >
                      <InputLabel>Province</InputLabel>
                      <Select
                        value={draftFilter.province}
                        label="Province"
                        onChange={(event) =>
                          setDraftFilter((prev) => ({
                            ...prev,
                            province: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        {options.provinces.map((item) => (
                          <MenuItem
                            key={item}
                            value={item}
                          >
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl
                      size="small"
                      sx={{ minWidth: 220 }}
                    >
                      <InputLabel>City</InputLabel>
                      <Select
                        value={draftFilter.city}
                        label="City"
                        onChange={(event) =>
                          setDraftFilter((prev) => ({
                            ...prev,
                            city: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        {options.cities.map((item) => (
                          <MenuItem
                            key={item}
                            value={item}
                          >
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleApply}
                    >
                      Apply
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleReset}
                    >
                      Reset
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleExport}
                      disabled={exporting}
                    >
                      {exporting ? "Queuing..." : "Export CSV"}
                    </Button>
                  </Box>
                </Stack>
              </Collapse>
            </Stack>
          </Card>

          <Card title={`Call List (${total})`}>
            <Box sx={{ p: 2 }}>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <DataTable
                    data={records}
                    columns={columns}
                    allowSort
                    onSortChange={handleSortChange}
                    orderBy={orderBy}
                    order={order}
                    tableHeight="62vh"
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ alignSelf: "center" }}
                    >
                      {total} calls
                    </Typography>
                    <TablePagination
                      component="div"
                      count={total}
                      page={pageNumber}
                      onPageChange={(event, newPage) => setPageNumber(newPage)}
                      rowsPerPage={recordsPerPage}
                      onRowsPerPageChange={(event) => {
                        setRecordsPerPage(Number(event.target.value));
                        setPageNumber(0);
                      }}
                      rowsPerPageOptions={[25, 50, 100]}
                    />
                  </Box>
                </>
              )}
            </Box>
          </Card>
        </Box>
      </Page>

      <CallDocketModal
        call={selectedCall}
        open={docketOpen}
        onClose={handleDocketClose}
        onRefresh={loadPage}
      />
    </>
  );
};

export default CallList;
